import {EDeviceEventLevel, getDeviceEventLevel} from "contracts/EDeviceEventLevel";
import {getDeviceEventSubTypeLabel} from "contracts/EDeviceEventSubType";
import {EDeviceEventType} from "contracts/EDeviceEventType";
import {IReportConfig} from "contracts/holotrak/IReportConfig";
import dayjs from "dayjs";
import {jsonArrayMember, jsonMember, jsonObject, TypedJSON} from "typedjson";
import {EventData, EventDataSerializer} from "./eventData";
import {EventTags} from "./eventTags";
import {EventUpdate, EventUpdateSerializer} from "./eventUpdate";

@jsonObject()
export class DeviceEvent {
    @jsonMember(String)
    subject: string;
    @jsonMember(String)
    message: string;

    @jsonMember(() => EventData, {
        deserializer: (value: any) => {
            try {
                return value && EventDataSerializer.parse({
                    ...value,
                    data: JSON.stringify(value, null, 2)
                });
            } catch (exception) {
                if (`${value}`.startsWith('+')) {
                    return EventDataSerializer.parse({data: value});
                } else {
                    return new EventData();
                }
            }
        }
    })
    data: EventData;

    @jsonMember(String)
    deviceId: string;

    @jsonMember(() => EventTags)
    eventTags: EventTags;

    @jsonMember(String)
    sourceType: string;
    @jsonMember(String)
    sourceId: string;
    @jsonMember(String)
    applicationId: string;
    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    creationDate: dayjs.Dayjs;
    @jsonMember(String)
    lastUpdated: string;

    @jsonArrayMember(EventUpdate, {
        deserializer: (value: any) => {
            return value && value.map((item: any) => EventUpdateSerializer.parse(item));
        }
    })
    updates: EventUpdate[];

    @jsonMember(String)
    eventId: string;

    @jsonMember(String)
    id: string;

    @jsonMember(() => EDeviceEventLevel, {
        deserializer: (value: unknown) => {
            return value && getDeviceEventLevel(value as string);
        }
    })
    level: EDeviceEventLevel;

    @jsonMember(String)
    state: string;
    @jsonMember(String)
    deviceName: string;
    @jsonMember(String)
    sourceName: string;

    _sNo: number = 0;

    get sNo(): string {
        return this._sNo.toString().padStart(4, '0');
    }

    set sNo(value: string) {
        this._sNo = parseInt(value, 10);
    }

    get coreUpdate(): EventUpdate {
        if (this.updates.length > 0) {
            return this.updates[0];
        } else {
            return new EventUpdate();
        }
    }

    get isStandard(): boolean {
        const nonStandardReportTypes = DeviceEvent.getNonStandardReportTypes();
        return nonStandardReportTypes.indexOf(this.eventTags.reportType) === -1;
    }

    get flightMode(): IReportConfig {
        switch (this.coreUpdate?.data?.in_flight_mode) {
            case true:
                return {
                    title: 'Flight Mode On',
                    shortTitle: 'FLIGHTON',
                    iconType: 'flight',
                    color: 'black',
                    enabled: true,
                };
            case false:
                return {
                    title: 'Flight Mode Off',
                    shortTitle: 'FLIGHTOFF',
                    iconType: 'flight',
                    color: 'black',
                    enabled: false,
                };
            default:
                return {
                    title: 'Flight Mode Unknown',
                    shortTitle: 'UNKNOWN',
                    color: 'black',
                    enabled: null,
                };
        }
    }

    get lightMode(): IReportConfig {
        switch (this.coreUpdate?.data?.is_light_detected) {
            case true:
                return {
                    title: 'Light Mode On',
                    shortTitle: 'LIGHTON',
                    iconType: 'light',
                    color: 'lime',
                    enabled: true,
                };
            case false:
                return {
                    title: 'Light Mode Off',
                    shortTitle: 'LIGHTOFF',
                    iconType: 'light',
                    color: 'grey',
                    enabled: false,
                };
            default:
                return {
                    title: 'Light Mode Unknown',
                    shortTitle: 'UNKNOWN',
                    iconType: 'light',
                    color: 'cyan',
                    enabled: null,
                };
        }
    }

    get reportConfig(): IReportConfig {
        let reportType: IReportConfig;

        switch (true) {
            case this.eventTags.reportType.startsWith('+BUFF'):
                reportType = {
                    title: 'Buffered Report',
                    shortTitle: 'BUFF',
                    color: 'black',
                };
                break;

            case this.eventTags.reportType.startsWith('+RESP'):
            case this.eventTags.reportType.startsWith('CALAMP-STANDARD'):
            case this.eventTags.reportType.startsWith('YABBY-DIGITAL-MATTER'):
                reportType = {
                    title: 'Realtime Report',
                    shortTitle: 'RT',
                    color: 'black',
                };
                break;

            default:
                reportType = {
                    title: 'Standard Report',
                    shortTitle: 'STD',
                    color: 'black',
                };
                break;
        }

        return reportType;
    }

    get messageText(): string {
        if (this.message) {
            return this.message;
        } else {
            switch (this.eventTags.type) {
                case EDeviceEventType.COMMAND:
                case EDeviceEventType.DEBUG:
                    return `#${this.sNo} ${getDeviceEventSubTypeLabel(this.eventTags.sub_type)}`;

                case EDeviceEventType.STATUS:
                default:
                    return `Status Report #${this.sNo}`;
            }
        }
    }

    static getNonStandardReportTypes() {
        return [
            'CALAMP-STANDARD',
            'YABBY-DIGITAL-MATTER',
            '+RESP:GTFRI',
            '+BUFF:GTFRI',
            '+RESP:GTFMD',
            '+BUFF:GTFMD',
        ];
    }
}

@jsonObject()
export class DeviceEventPaginationResponse {
    @jsonMember(Number)
    count: number;

    @jsonMember(Number)
    totalCount: number;

    @jsonMember(Number)
    page: number;

    @jsonMember(Number)
    perPage: number;

    @jsonMember(String)
    sortField: string;

    @jsonMember(String)
    sortDirection: string;

    @jsonArrayMember(DeviceEvent)
    items: DeviceEvent[];

    @jsonMember(String)
    findMethod: string;
}

export const DeviceEventSerializer = new TypedJSON(DeviceEvent);
